import React, { useState, useCallback } from 'react'
import {
  Box,
  IconButton,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Slide,
} from '@mui/material'
import { TransitionProps } from '@mui/material/transitions'
import { X } from 'lucide-react'
import { useHaptic } from '../../../hooks/useHaptic'

/**
 * Batch action configuration
 */
export interface BatchAction {
  /** Unique identifier */
  id: string

  /** Display label */
  label: string

  /** Icon component */
  icon: React.ComponentType<{ size?: string | number }>

  /** Click handler - receives selected IDs */
  onClick: (selectedIds: string[]) => void

  /** Visual variant */
  variant?: 'default' | 'destructive'

  /** Whether to show confirmation dialog */
  requiresConfirmation?: boolean
}

/**
 * BatchActionToolbar props
 */
export interface BatchActionToolbarProps {
  /** Number of selected items */
  selectedCount: number

  /** Total number of items */
  totalCount: number

  /** Array of selected item IDs */
  selectedIds: string[]

  /** Available batch actions */
  actions: BatchAction[]

  /** Callback when cancel is clicked */
  onCancel: () => void

  /** Callback when select all is clicked */
  onSelectAll: () => void

  /** Callback when clear selection is clicked */
  onClearSelection: () => void
}

/**
 * Slide up transition for the toolbar
 */
const SlideTransition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />
})

/**
 * BatchActionToolbar Component
 *
 * Fixed bottom toolbar for batch operations in selection mode.
 * Shows when items are selected and provides actions like delete, export, etc.
 *
 * @example
 * ```tsx
 * <BatchActionToolbar
 *   selectedCount={3}
 *   totalCount={10}
 *   selectedIds={['1', '2', '3']}
 *   actions={[
 *     { id: 'delete', label: 'Delete', icon: Trash2, onClick: handleDelete, variant: 'destructive' },
 *     { id: 'export', label: 'Export', icon: Download, onClick: handleExport },
 *   ]}
 *   onCancel={exitSelectionMode}
 *   onSelectAll={selectAll}
 *   onClearSelection={clearSelection}
 * />
 * ```
 */
export function BatchActionToolbar({
  selectedCount,
  totalCount,
  selectedIds,
  actions,
  onCancel,
  onSelectAll,
  onClearSelection,
}: BatchActionToolbarProps): JSX.Element | null {
  const [confirmAction, setConfirmAction] = useState<BatchAction | null>(null)
  const { light } = useHaptic()

  /**
   * Handle action click
   */
  const handleActionClick = useCallback(
    (action: BatchAction) => {
      light()

      if (action.requiresConfirmation) {
        setConfirmAction(action)
      } else {
        action.onClick(selectedIds)
      }
    },
    [light, selectedIds]
  )

  /**
   * Handle confirmation
   */
  const handleConfirm = useCallback(() => {
    if (confirmAction) {
      confirmAction.onClick(selectedIds)
      setConfirmAction(null)
    }
  }, [confirmAction, selectedIds])

  /**
   * Handle cancel confirmation
   */
  const handleCancelConfirm = useCallback(() => {
    setConfirmAction(null)
  }, [])

  /**
   * Handle select all / clear selection toggle
   */
  const handleSelectToggle = useCallback(() => {
    light()
    if (selectedCount === totalCount) {
      onClearSelection()
    } else {
      onSelectAll()
    }
  }, [light, selectedCount, totalCount, onSelectAll, onClearSelection])

  /**
   * Handle cancel selection mode
   */
  const handleCancel = useCallback(() => {
    light()
    onCancel()
  }, [light, onCancel])

  // Don't render if nothing is selected
  if (selectedCount === 0) {
    return null
  }

  const allSelected = selectedCount === totalCount

  return (
    <>
      <Box
        role="toolbar"
        aria-label="Batch actions"
        sx={{
          position: 'fixed',
          bottom: 56, // Above bottom navigation
          left: 0,
          right: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1,
          backgroundColor: 'background.paper',
          borderTop: 1,
          borderColor: 'divider',
          zIndex: 1100,
          boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
          pb: 'calc(8px + env(safe-area-inset-bottom, 0px))',
        }}
      >
        {/* Left section: Cancel and selection info */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            onClick={handleCancel}
            aria-label="Cancel selection"
            size="small"
          >
            <X size={20} />
          </IconButton>

          <Typography
            variant="body2"
            fontWeight={500}
            aria-live="polite"
          >
            {selectedCount} selected
          </Typography>
        </Box>

        {/* Center section: Select all / Clear */}
        <Button
          variant="text"
          size="small"
          onClick={handleSelectToggle}
          sx={{ textTransform: 'none' }}
        >
          {allSelected ? 'Clear selection' : 'Select all'}
        </Button>

        {/* Right section: Action buttons */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {actions.map((action) => {
            const Icon = action.icon
            const isDestructive = action.variant === 'destructive'

            return (
              <IconButton
                key={action.id}
                onClick={() => handleActionClick(action)}
                aria-label={action.label}
                data-variant={action.variant || 'default'}
                size="small"
                sx={{
                  color: isDestructive ? 'error.main' : 'primary.main',
                }}
              >
                <Icon size={20} />
              </IconButton>
            )
          })}
        </Box>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog
        open={Boolean(confirmAction)}
        onClose={handleCancelConfirm}
        TransitionComponent={SlideTransition}
      >
        <DialogTitle>Confirm action</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to {confirmAction?.label.toLowerCase()}{' '}
            {selectedCount} {selectedCount === 1 ? 'item' : 'items'}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelConfirm}>Cancel</Button>
          <Button
            onClick={handleConfirm}
            color={confirmAction?.variant === 'destructive' ? 'error' : 'primary'}
            variant="contained"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
